// Interview de Vincent Caure — Député des Français établis hors de France (3e circonscription),
// Président de la Commission des lois de l'Assemblée nationale. Riga, septembre 2026.

import { caureQuestions } from './caureQuestions';

export const caureInterviewUrl = 'https://bcfl.eu/interview-vincent-caure';

const ASSETS = 'https://customer-assets-39nsmqrw.emergentagent.net/job_09571151-f654-4506-aa32-05da61a50408/artifacts';

export const caureHeroPhoto = {
  src: `${ASSETS}/s72ctlis_Vincent%20Caure.webp`,
  alt: {
    fr: "Vincent Caure, député des Français établis hors de France, dans l'hémicycle de l'Assemblée nationale",
    en: 'Vincent Caure, Member of the French National Assembly for French citizens abroad, in the chamber of the National Assembly',
    lv: 'Vincent Caure, ārvalstīs dzīvojošo Francijas pilsoņu deputāts, Francijas Nacionālās asamblejas sēžu zālē',
  },
};

// `afterQuestion` = index (0-based) de la question après laquelle la photo apparaît
export const caureGallery = [
  {
    afterQuestion: 0,
    orientation: 'landscape',
    src: `${ASSETS}/z8ox2w47_Caure%201.jpg`,
    caption: {
      fr: 'Vincent Caure, écharpe tricolore, lors d’une cérémonie de rentrée aux côtés de la communauté française et francophone de Riga.',
      en: 'Vincent Caure, wearing the tricolour sash, at a back-to-school ceremony alongside the French and French-speaking community in Riga.',
      lv: 'Vincent Caure ar trīskrāsu lenti mācību gada sākuma svinībās kopā ar Francijas un frankofono kopienu Rīgā.',
    },
  },
  {
    afterQuestion: 2,
    orientation: 'landscape',
    src: `${ASSETS}/y7qdwwgo_Caure%204.jpg`,
    caption: {
      fr: 'Vincent Caure lors de sa visite à Riga, devant les drapeaux français, européen et letton, aux côtés de partenaires institutionnels et de représentants du Business Club France Latvia.',
      en: 'Vincent Caure during his visit to Riga, in front of the French, European and Latvian flags, alongside institutional partners and representatives of the Business Club France Latvia.',
      lv: 'Vincent Caure vizītes Rīgā laikā pie Francijas, Eiropas un Latvijas karogiem kopā ar institucionālajiem partneriem un Business Club France Latvia pārstāvjiem.',
    },
  },
  {
    afterQuestion: 5,
    orientation: 'portrait',
    src: `${ASSETS}/j6e18t22_Caure%202.jpg`,
    caption: {
      fr: 'Vincent Caure, écharpe tricolore, lors d’une réception avec la communauté française de Riga.',
      en: 'Vincent Caure, wearing the tricolour sash, at a reception with the French community in Riga.',
      lv: 'Vincent Caure ar trīskrāsu lenti pieņemšanā kopā ar Francijas kopienu Rīgā.',
    },
  },
  {
    afterQuestion: 7,
    orientation: 'landscape',
    src: `${ASSETS}/qpejt40y_Caure%203.jpg`,
    caption: {
      fr: 'Temps d’échange avec des membres de la communauté française et des entrepreneurs établis à Riga.',
      en: 'Discussion time with members of the French community and entrepreneurs based in Riga.',
      lv: 'Sarunas ar Francijas kopienas pārstāvjiem un Rīgā strādājošiem uzņēmējiem.',
    },
  },
];

export const caureInterview = {
  fr: {
    seo: {
      title: 'Vincent Caure : France, Lettonie et opportunités franco-baltes | BCFL',
      description:
        'Interview de Vincent Caure par le Business Club France Latvia : entreprises françaises, pays baltes, coopération économique, défense, innovation et rôle de la Lettonie en Europe du Nord.',
    },
    teaser: {
      eyebrow: 'Entretien / Interview',
      badge: 'Nouveau',
      title: 'Vincent Caure : France, Lettonie et nouvelles opportunités franco-baltes',
      summary:
        "À l’occasion de sa visite à Riga, Vincent Caure, député des Français établis en Europe du Nord et Président de la Commission des lois de l’Assemblée nationale, répond aux questions du Business Club France Latvia sur la Lettonie, les pays baltes, les entreprises françaises et les nouvelles perspectives de coopération économique.",
      cta: 'Lire l’interview',
    },
    hero: {
      label: 'Interview',
      title: 'Entretien avec Vincent Caure',
      roles: [
        'Député de la 3e circonscription des Français établis hors de France',
        'Président de la Commission des lois de l’Assemblée nationale',
      ],
      dateline: 'Riga — Septembre 2026',
    },
    intro: [
      "À l’occasion de sa visite à Riga, le Business Club France Latvia a souhaité échanger avec Vincent Caure sur la place de la Lettonie et des pays baltes dans les relations économiques françaises, les opportunités pour les entreprises françaises, le rôle des entrepreneurs établis localement et l’évolution du contexte stratégique en Europe du Nord.",
      'Au fil de cet entretien, Vincent Caure revient également sur le rôle que peuvent jouer les réseaux économiques locaux pour rapprocher entreprises, institutions et décideurs français et lettons.',
    ],
    speaker: 'Vincent Caure',
    interviewer: 'Business Club France Latvia',
    ctaBack: 'Retour à l’accueil',
    closing: {
      title: 'Vous souhaitez développer une activité ou identifier des partenaires en Lettonie ?',
      text:
        'Le Business Club France Latvia facilite les mises en relation entre entreprises, entrepreneurs, institutions et acteurs économiques français et lettons.',
      cta: 'Prendre contact avec le Business Club',
    },
    share: {
      heading: 'Partager cette interview',
      linkedin: 'Partager sur LinkedIn',
      twitter: 'Partager sur X',
      shareText: 'Vincent Caure : France, Lettonie et nouvelles opportunités franco-baltes — Interview Business Club France Latvia',
    },
  },

  en: {
    seo: {
      title: 'Vincent Caure: France, Latvia and Franco-Baltic opportunities | BCFL',
      description:
        "Interview with Vincent Caure by the Business Club France Latvia: French companies, the Baltic states, economic cooperation, defence, innovation and Latvia's role in Northern Europe.",
    },
    teaser: {
      eyebrow: 'Interview',
      badge: 'New',
      title: 'Vincent Caure: France, Latvia and new Franco-Baltic opportunities',
      summary:
        "During his visit to Riga, Vincent Caure, Member of the French National Assembly for French citizens living in Northern Europe and Chair of the Assembly's Law Committee, answers the Business Club France Latvia's questions on Latvia, the Baltic states, French companies and new prospects for economic cooperation.",
      cta: 'Read the interview',
    },
    hero: {
      label: 'Interview',
      title: 'A conversation with Vincent Caure',
      roles: [
        'Member of the National Assembly for the 3rd constituency of French citizens living abroad',
        'Chair of the Law Committee of the French National Assembly',
      ],
      dateline: 'Riga — September 2026',
    },
    intro: [
      "On the occasion of his visit to Riga, the Business Club France Latvia sat down with Vincent Caure to discuss the place of Latvia and the Baltic states in France's economic relations, the opportunities open to French companies, the role of locally established entrepreneurs and the changing strategic context in Northern Europe.",
      'Throughout the conversation, Vincent Caure also reflects on the role local business networks can play in bringing together French and Latvian companies, institutions and decision-makers.',
    ],
    speaker: 'Vincent Caure',
    interviewer: 'Business Club France Latvia',
    ctaBack: 'Back to home',
    closing: {
      title: 'Looking to develop a business or identify partners in Latvia?',
      text:
        'The Business Club France Latvia connects French and Latvian companies, entrepreneurs, institutions and economic stakeholders.',
      cta: 'Get in touch with the Business Club',
    },
    share: {
      heading: 'Share this interview',
      linkedin: 'Share on LinkedIn',
      twitter: 'Share on X',
      shareText: 'Vincent Caure: France, Latvia and new Franco-Baltic opportunities — Business Club France Latvia interview',
    },
  },

  lv: {
    seo: {
      title: 'Vincent Caure: Francija, Latvija un Francijas–Baltijas iespējas | BCFL',
      description:
        'Business Club France Latvia intervija ar Vincent Caure: Francijas uzņēmumi, Baltijas valstis, ekonomiskā sadarbība, aizsardzība, inovācijas un Latvijas loma Ziemeļeiropā.',
    },
    teaser: {
      eyebrow: 'Intervija',
      badge: 'Jaunums',
      title: 'Vincent Caure: Francija, Latvija un jaunas Francijas–Baltijas iespējas',
      summary:
        'Vizītes Rīgā laikā Vincent Caure, Francijas Nacionālās asamblejas deputāts, kurš pārstāv Ziemeļeiropā dzīvojošos Francijas pilsoņus, un Nacionālās asamblejas Likumu komisijas priekšsēdētājs, atbild uz Business Club France Latvia jautājumiem par Latviju, Baltijas valstīm, Francijas uzņēmumiem un jaunām ekonomiskās sadarbības perspektīvām.',
      cta: 'Lasīt interviju',
    },
    hero: {
      label: 'Intervija',
      title: 'Saruna ar Vincent Caure',
      roles: [
        'Francijas Nacionālās asamblejas deputāts, ārvalstīs dzīvojošo Francijas pilsoņu 3. vēlēšanu apgabals',
        'Francijas Nacionālās asamblejas Likumu komisijas priekšsēdētājs',
      ],
      dateline: 'Rīga — 2026. gada septembris',
    },
    intro: [
      'Vincent Caure vizītes Rīgā laikā Business Club France Latvia vēlējās ar viņu pārrunāt Latvijas un Baltijas valstu vietu Francijas ekonomiskajās attiecībās, iespējas Francijas uzņēmumiem, uz vietas strādājošo uzņēmēju lomu un stratēģiskā konteksta attīstību Ziemeļeiropā.',
      'Sarunas gaitā Vincent Caure pievēršas arī tam, kādu lomu vietējie biznesa tīkli var spēlēt, tuvinot Francijas un Latvijas uzņēmumus, institūcijas un lēmumu pieņēmējus.',
    ],
    speaker: 'Vincent Caure',
    interviewer: 'Business Club France Latvia',
    ctaBack: 'Atpakaļ uz sākumu',
    closing: {
      title: 'Vēlaties attīstīt uzņēmējdarbību vai atrast partnerus Latvijā?',
      text:
        'Business Club France Latvia veicina kontaktu veidošanu starp Francijas un Latvijas uzņēmumiem, uzņēmējiem, institūcijām un ekonomikas dalībniekiem.',
      cta: 'Sazināties ar Business Club',
    },
    share: {
      heading: 'Dalīties ar šo interviju',
      linkedin: 'Dalīties LinkedIn',
      twitter: 'Dalīties X',
      shareText: 'Vincent Caure: Francija, Latvija un jaunas Francijas–Baltijas iespējas — Business Club France Latvia intervija',
    },
  },
};

Object.keys(caureInterview).forEach((lang) => {
  caureInterview[lang].questions = caureQuestions[lang];
});

export default caureInterview;
